import { useState } from "react";
import Button from "../../../../components/EcommerceActionButton/Button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../components/Sheet";
import { ProductSelector } from "../ProductSelector";
import { AccessorySelector } from "../AccessorySelector";
import {
  Dialog as AppDialog,
  DialogActions as AppDialogActions,
  DialogContent as AppDialogContent,
  DialogRow as AppDialogRow,
  DialogTitle as AppDialogTitle,
} from "../../../../components/Dialog/Dialog";
import {
  useGetClientsList,
  useGetUserProfile,
} from "../../../../utils/react-query/queryEcommerce/queries";
import { useCreateSalespersonQuote } from "../../../../utils/react-query/querySalesperson/queries";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import {
  transformDate,
  formatLocalDateTimeInput,
} from "../../../../utils/dateUtils";
import { useQuoteState } from "./hooks/useQuoteState";
import { useQuoteCalculations } from "./hooks/useQuoteCalculations";
import { QuoteClientSection } from "./components/QuoteClientSection";
import { QuoteProductsSection } from "./components/QuoteProductsSection";
import { QuoteAccessoriesSection } from "./components/QuoteAccessoriesSection";
import { QuoteTotalsSection } from "./components/QuoteTotalsSection";
import styles from "./QuoteForm.module.scss";

interface QuoteFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (quotation: Quotation) => void;
  editingQuotation?: Quotation;
}

export function QuoteForm({
  isOpen,
  onClose,
  onSave,
  editingQuotation,
}: QuoteFormProps) {
  const { data: userProfile } = useGetUserProfile();
  const { data: clientsListData } = useGetClientsList();
  const { mutate: createSalespersonQuote, isLoading: isSavingQuote } =
    useCreateSalespersonQuote();
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showAccessorySelector, setShowAccessorySelector] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    quotation: Quotation;
    totals: {
      subtotal: number;
      vat19: number;
      total: number;
      discountTotal: number;
      totalM2?: number;
    };
  } | null>(null);
  const clients: ClientProfile[] = clientsListData || [];

  const {
    selectedClientId,
    currentQuotation,
    setCurrentQuotation,
    handleSelectClient,
    handleSelectProduct,
    handleSelectAccessory,
    removeProduct,
    updateProductQuantity,
    updateProductDiscount,
    updateAccessoryDiscount,
    updateProductMeasure,
    MAX_DISCOUNT_PERCENT,
  } = useQuoteState({
    editingQuotation,
    userProfileEmail: userProfile?.email,
    clients,
  });

  const {
    getDiscountedProductTotal,
    getProductAccessoryNeeds: getAccessoryNeeds,
    calculateTotals,
    buildProductsWithAccessoryQuantities,
    calculateTotalM2,
    accessoriesCalculation,
    totals,
  } = useQuoteCalculations(currentQuotation);

  /**
   * Handle saving the quotation
   */
  const handleSave = async () => {
    const calculatedTotals = calculateTotals();
    const productsWithAccessoryQuantities =
      buildProductsWithAccessoryQuantities();
    const totalM2 = calculateTotalM2();

    const creationDate = currentQuotation.date || formatLocalDateTimeInput();
    const newQuotation: Quotation = {
      ...(currentQuotation as Quotation),
      date: creationDate,
      products: productsWithAccessoryQuantities,
      totalM2,
      subtotal: calculatedTotals.subtotal,
      total: calculatedTotals.total,
      vat19: calculatedTotals.vat19,
    };

    setConfirmationData({
      quotation: newQuotation,
      totals: {
        ...calculatedTotals,
        totalM2,
      },
    });
  };

  const handleConfirmSave = () => {
    if (!confirmationData) return;

    const { quotation, totals: confirmTotals } = confirmationData;

    createSalespersonQuote(
      { quotation, totals: confirmTotals },
      {
        onSuccess: (data) => {
          const pdfUrl = data?.location || data?.pdfUrl || data?.url;
          onSave({ ...quotation, pdfUrl });
          setConfirmationData(null);
        },
        onError: () => {
          setConfirmationData(null);
        },
      }
    );
  };

  /**
   * Prepare IDs for selector components
   */
  const selectedProductIds = currentQuotation.products?.map((p) => p.id) || [];
  const selectedAccessoryIds =
    currentQuotation.accessories?.map((a) => a.id) || [];

  return (
    <>
      {/* Sheet for the Form */}
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className={styles.sheetContent}>
          <SheetHeader className={styles.sheetHeader}>
            <SheetTitle className={styles.sheetTitle}>
              {editingQuotation ? "Editar Cotización" : "Nueva Cotización"}
            </SheetTitle>
          </SheetHeader>

          <div className={styles.formContent}>
            {/* Client Section */}
            <QuoteClientSection
              clients={clients}
              selectedClientId={selectedClientId}
              onSelectClient={handleSelectClient}
              clientName={currentQuotation.clientName || ""}
              onClientNameChange={(name) =>
                setCurrentQuotation({ ...currentQuotation, clientName: name })
              }
              taxId={currentQuotation.taxId || ""}
              onTaxIdChange={(taxId) =>
                setCurrentQuotation({ ...currentQuotation, taxId })
              }
              cityAddress={currentQuotation.cityAddress || ""}
              onCityAddressChange={(address) =>
                setCurrentQuotation({
                  ...currentQuotation,
                  cityAddress: address,
                })
              }
              phone={currentQuotation.phone || ""}
              onPhoneChange={(phone) =>
                setCurrentQuotation({ ...currentQuotation, phone })
              }
              clientEmail={currentQuotation.clientEmail || ""}
              onClientEmailChange={(email) =>
                setCurrentQuotation({ ...currentQuotation, clientEmail: email })
              }
              advisorEmail={currentQuotation.advisorEmail || ""}
            />

            {/* Products Section */}
            <QuoteProductsSection
              products={currentQuotation.products || []}
              onAddProduct={() => setShowProductSelector(true)}
              onRemoveProduct={(id) => removeProduct("products", id)}
              onUpdateQuantity={(id, quantity) =>
                updateProductQuantity("products", id, quantity)
              }
              onUpdateDiscount={(id, discount) =>
                updateProductDiscount(id, discount)
              }
              onUpdateMeasure={(id, value, unit) =>
                updateProductMeasure(id, value, unit)
              }
              getDiscountedTotal={getDiscountedProductTotal}
              getAccessoryNeeds={getAccessoryNeeds}
              maxDiscountPercent={MAX_DISCOUNT_PERCENT}
            />

            {/* Accessories Section */}
            <QuoteAccessoriesSection
              accessories={currentQuotation.accessories || []}
              autoAccessories={accessoriesCalculation.autoAccessories}
              totalM2={accessoriesCalculation.totalM2}
              onAddAccessory={() => setShowAccessorySelector(true)}
              onRemoveAccessory={(id) => removeProduct("accessories", id)}
              onUpdateQuantity={(id, quantity) =>
                updateProductQuantity("accessories", id, quantity)
              }
              onUpdateDiscount={(id, discount) =>
                updateAccessoryDiscount(id, discount)
              }
              maxDiscountPercent={MAX_DISCOUNT_PERCENT}
            />

            {/* Comments */}
            <div className={styles.formGroup}>
              <span className={styles.label}>Comentarios</span>
              <textarea
                id="comentarios"
                value={currentQuotation.notes || ""}
                onChange={(e) =>
                  setCurrentQuotation({
                    ...currentQuotation,
                    notes: e.target.value,
                  })
                }
                rows={3}
                className={styles.textarea}
                placeholder="Observaciones adicionales..."
              />
            </div>

            {/* Totals Section */}
            <QuoteTotalsSection
              shipping={currentQuotation.shipping || 0}
              onShippingChange={(value) =>
                setCurrentQuotation({ ...currentQuotation, shipping: value })
              }
              others={currentQuotation.others || 0}
              onOthersChange={(value) =>
                setCurrentQuotation({ ...currentQuotation, others: value })
              }
              subtotal={totals.subtotal}
              discountTotal={totals.discountTotal}
              vat19={totals.vat19}
              total={totals.total}
            />

            {/* Actions */}
            <div className={styles.actionsSection}>
              <Button
                color="outlined"
                shape="little-rounded"
                onClick={onClose}
                disabled={isSavingQuote}
                className={styles.compactButton}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                shape="little-rounded"
                onClick={handleSave}
                disabled={
                  isSavingQuote ||
                  !currentQuotation.clientName ||
                  !currentQuotation.taxId
                }
                className={styles.compactButton}
              >
                {isSavingQuote ? "Guardando..." : "Guardar Cotización"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialog */}
      <AppDialog
        open={Boolean(confirmationData)}
        onOpenChange={(open) => {
          if (!open && !isSavingQuote) {
            setConfirmationData(null);
          }
        }}
      >
        <AppDialogTitle>Confirmar envío de cotización</AppDialogTitle>
        <AppDialogContent>
          <AppDialogRow>
            <span>Cliente:</span>
            <strong>{confirmationData?.quotation.clientName}</strong>
          </AppDialogRow>
          <AppDialogRow>
            <span>Fecha creación:</span>
            <strong>
              {confirmationData?.quotation.date
                ? transformDate(confirmationData.quotation.date)
                : "-"}
            </strong>
          </AppDialogRow>
          <AppDialogRow>
            <span>Valor despacho:</span>
            <strong>
              {formatNumberToPrice(
                Number(confirmationData?.quotation.shipping || 0)
              )}
            </strong>
          </AppDialogRow>
          <AppDialogRow>
            <span>Otros:</span>
            <strong>
              {formatNumberToPrice(
                Number(confirmationData?.quotation.others || 0)
              )}
            </strong>
          </AppDialogRow>
          <AppDialogRow>
            <span>Total:</span>
            <strong>
              {formatNumberToPrice(confirmationData?.totals.total || 0)}
            </strong>
          </AppDialogRow>

          <AppDialogActions>
            <Button
              color="outlined"
              shape="little-rounded"
              onClick={() => setConfirmationData(null)}
              disabled={isSavingQuote}
              className={styles.compactButton}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              shape="little-rounded"
              onClick={handleConfirmSave}
              isLoading={isSavingQuote}
              disabled={isSavingQuote}
              className={styles.compactButton}
            >
              Confirmar y Guardar
            </Button>
          </AppDialogActions>
        </AppDialogContent>
      </AppDialog>

      {/* Product Selector Modal */}
      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelectProduct={handleSelectProduct}
        selectedProducts={selectedProductIds}
      />

      {/* Accessory Selector Modal */}
      <AccessorySelector
        isOpen={showAccessorySelector}
        onClose={() => setShowAccessorySelector(false)}
        onSelectAccessory={handleSelectAccessory}
        selectedAccessories={selectedAccessoryIds}
      />
    </>
  );
}
